/**
 * Pure helpers for the People Hierarchy page.
 *
 * The page loads the reporting forest once (GET /organisation/hierarchy) and
 * works on it client-side: index by id, locate/expand a person, and derive the
 * department view. Tree "keys" are strings so employee rows and grouping rows
 * (departments, "not assigned") can share one expand/collapse state.
 */
import { normalizeEmployeeRole } from "../employeeRoleConstants";

export const NO_DEPARTMENT = "No department";

export const empKey = (id) => `emp:${id}`;
export const groupKey = (id) => `grp:${id}`;

/**
 * Build { byId: Map<id, {node, parentId}>, list: node[], conflicts } from the forest.
 *
 * The tree is driven only by the reporting relationship: one employee, one
 * manager. If the data ever lists someone under a second manager, the first
 * placement is kept and the extra one is reported in `conflicts` (and skipped
 * when rendering) instead of drawing the same person in two branches.
 */
export function indexForest(forest) {
  const byId = new Map();
  const list = [];
  const conflicts = [];
  const walk = (node, parentId) => {
    if (!node?.employee_id) return;
    const id = String(node.employee_id);
    const existing = byId.get(id);
    if (existing) {
      if (existing.parentId !== parentId) {
        conflicts.push({ employeeId: id, name: node.employee_name, keptParentId: existing.parentId, extraParentId: parentId });
      }
      return;
    }
    byId.set(id, { node, parentId });
    list.push(node);
    (node.children || []).forEach((c) => walk(c, id));
  };
  (forest || []).forEach((root) => walk(root, null));
  return { byId, list, conflicts };
}

/** Ancestors of an employee, top-most first (excluding the employee). */
export function ancestorIds(index, employeeId) {
  const out = [];
  let cur = index.byId.get(String(employeeId))?.parentId;
  const seen = new Set();
  while (cur && !seen.has(cur)) {
    seen.add(cur);
    out.unshift(cur);
    cur = index.byId.get(cur)?.parentId;
  }
  return out;
}

/** Every employee below this one (used to block circular manager picks). */
export function descendantIds(index, employeeId) {
  const out = new Set();
  const start = index.byId.get(String(employeeId))?.node;
  const stack = [...(start?.children || [])];
  while (stack.length) {
    const n = stack.pop();
    const id = String(n.employee_id);
    if (out.has(id)) continue;
    out.add(id);
    stack.push(...(n.children || []));
  }
  return out;
}

/**
 * Role shown in the hierarchy. `node.role` is the effective access role from the
 * hierarchy API (linked login account's role, else employees.role) — never the
 * designation. An Employee-role person who leads a team counts as Manager, since
 * the app lets Employee-role people be reporting managers.
 * Returns { role: "admin" | "hr" | "manager" | "employee", inferred: boolean }.
 */
export function hierarchyRoleOf(node) {
  const role = normalizeEmployeeRole(node?.role);
  if (role === "employee" && (node?.children || []).length > 0) {
    return { role: "manager", inferred: true };
  }
  return { role, inferred: false };
}

/** Admin / HR / Manager (explicit or by leading a team) — shown as eligible managers. */
export function isEligibleManagerNode(node) {
  return hierarchyRoleOf(node).role !== "employee";
}

export function isAdminNode(node) {
  return normalizeEmployeeRole(node?.role) === "admin";
}

/** First admin at the top of the org — target for "Report directly to Admin". */
export function findTopAdmin(forest) {
  return (forest || []).find(isAdminNode) || null;
}

/** Local search by name or employee code. */
export function searchIndex(index, query, limit = 12) {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return [];
  const starts = [];
  const contains = [];
  for (const n of index.list) {
    const name = String(n.employee_name || "").toLowerCase();
    const code = String(n.employee_code || "").toLowerCase();
    if (code === q || name.startsWith(q) || code.startsWith(q)) starts.push(n);
    else if (name.includes(q) || code.includes(q)) contains.push(n);
    if (starts.length >= limit) break;
  }
  return [...starts, ...contains].slice(0, limit);
}

export function departmentsOf(index) {
  const set = new Set(index.list.map((n) => n.department || NO_DEPARTMENT));
  return [...set].sort((a, b) => {
    if (a === NO_DEPARTMENT) return 1;
    if (b === NO_DEPARTMENT) return -1;
    return a.localeCompare(b);
  });
}

/* ── View models ──────────────────────────────────────────────
   A view item is either
     { type: "emp", key, node, children: item[] }
     { type: "group", key, label, sub, count, children: item[] }
   Children are nested view items so the renderer never touches raw nodes. */

/** `seen` makes sure each employee is emitted once per view, whatever the data says. */
function empItem(node, childrenOf, seen) {
  seen.add(String(node.employee_id));
  const children = childrenOf(node)
    .filter((c) => !seen.has(String(c.employee_id)))
    .map((c) => empItem(c, childrenOf, seen));
  return { type: "emp", key: empKey(node.employee_id), node, children };
}

const byChildren = (n) => n.children || [];

export const UNASSIGNED_KEY = groupKey("unassigned");

/**
 * Organization tree — built only from employee → reporting manager links.
 * Admins (top of the org) come first with everyone under them; every other
 * employee without a reporting manager goes into "Unassigned employees",
 * together with anyone who reports to them.
 */
export function buildOrgView(forest) {
  const roots = forest || [];
  const seen = new Set();
  const admins = roots.filter(isAdminNode);
  const unassigned = roots.filter((r) => !isAdminNode(r));

  const items = admins.map((r) => empItem(r, byChildren, seen));
  const unassignedItems = unassigned
    .filter((r) => !seen.has(String(r.employee_id)))
    .map((r) => empItem(r, byChildren, seen));
  if (unassignedItems.length) {
    items.push({
      type: "group",
      key: UNASSIGNED_KEY,
      label: `Unassigned employees (${unassignedItems.length})`,
      sub: "No reporting manager — select someone to assign one",
      count: unassignedItems.length,
      children: unassignedItems,
    });
  }
  return items;
}

/**
 * Department tree: inside a department, a person's parent is their nearest
 * manager from the same department; people whose manager sits elsewhere
 * become the department's top level.
 */
function departmentItems(index, dept) {
  const members = index.list.filter((n) => (n.department || NO_DEPARTMENT) === dept);
  const memberIds = new Set(members.map((n) => String(n.employee_id)));
  const kids = new Map();
  const tops = [];
  for (const n of members) {
    let p = index.byId.get(String(n.employee_id))?.parentId;
    const seen = new Set();
    while (p && !memberIds.has(p) && !seen.has(p)) {
      seen.add(p);
      p = index.byId.get(p)?.parentId;
    }
    if (p && memberIds.has(p)) {
      if (!kids.has(p)) kids.set(p, []);
      kids.get(p).push(n);
    } else {
      tops.push(n);
    }
  }
  const childrenOf = (n) => kids.get(String(n.employee_id)) || [];
  // Leaders (people with reports) first so the department head shows on top.
  tops.sort((a, b) => childrenOf(b).length - childrenOf(a).length);
  const seen = new Set();
  return { count: members.length, items: tops.map((n) => empItem(n, childrenOf, seen)) };
}

export function buildDepartmentView(index, department) {
  if (department && department !== "all") {
    return departmentItems(index, department).items;
  }
  return departmentsOf(index).map((dept) => {
    const { count, items } = departmentItems(index, dept);
    return {
      type: "group",
      key: groupKey(`dept:${dept}`),
      label: dept,
      sub: `${count} ${count === 1 ? "person" : "people"}`,
      count,
      children: items,
    };
  });
}

/** Keys from the top of a view down to (not including) the employee's row. */
export function pathKeysInView(items, employeeId) {
  const target = empKey(employeeId);
  const walk = (list, trail) => {
    for (const it of list) {
      if (it.key === target) return trail;
      const found = walk(it.children || [], [...trail, it]);
      if (found) return found;
    }
    return null;
  };
  return walk(items, []);
}
